import React, { useState } from "react"
import { IconPlus, IconStarFilled, IconUserPlus } from "@tabler/icons-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@love-irish/ui-components/dialog"
import { useSession } from "@love-irish/application-session-provider"
import { UpsellModal } from "@love-irish/upsell-modal"
import { useModalAnalytics } from "@love-irish/analytics"
import { useEditing } from "design_system/utils/editing"

import { renderTemplate, firstFormReference } from "./template"
import {
  ExampleToken,
  ExampleTokenContext,
  EXAMPLE_FILTERS,
} from "./example_tokens"

/** Contributing examples is gated to level 3+ (a light spam/quality bar). !blocked checks come later. */
export const MIN_EXAMPLE_LEVEL = 3

export interface AddExampleDraft {
  text: string
  translation: string
}

/** An existing example being edited (opens the modal in edit mode). */
export interface EditableExample {
  id?: string
  text: string
  translation: string
}

interface AddExampleModalProps extends ExampleTokenContext {
  open: boolean
  onOpenChange: (open: boolean) => void
  headword: string
  loginHref?: string
  arcadeHref?: string
  /** Present = edit mode: prefills the fields and shows a Delete link. */
  example?: EditableExample
  /** Create a new example. */
  onSubmit?: (draft: AddExampleDraft) => void | Promise<void>
  /** Save edits to `example`. */
  onUpdate?: (example: EditableExample, draft: AddExampleDraft) => void | Promise<void>
  /** Delete `example`. */
  onDelete?: (example: EditableExample) => void | Promise<void>
}

/**
 * The "add an example" modal: two columns — an editor with a live preview (usable by anyone, so they
 * can see how the dynamic strings render), and a scrollable reference of every supported dynamic
 * string with click-to-insert. A full-width action sits at the bottom: it submits when the viewer is
 * eligible (signed in, level 3+), otherwise it opens the shared upsell modal (sign up / level up).
 * Fires modal.opened / modal.submitted analytics so we can see who wants to contribute.
 */
export const AddExampleModal: React.FC<AddExampleModalProps> = ({
  open,
  onOpenChange,
  headword,
  context,
  tokens,
  loginHref = "/login",
  arcadeHref = "/arcade",
  example,
  onSubmit,
  onUpdate,
  onDelete,
}) => {
  const { loggedIn, currentUser } = useSession()
  const level = (currentUser as any)?.profile?.levelProgress?.currentLevel ?? 0
  const eligible = loggedIn && level >= MIN_EXAMPLE_LEVEL
  const editing = Boolean(example)

  const { trackSubmitted } = useModalAnalytics(editing ? "codex_edit_example" : "codex_add_example", open)

  const [text, setText] = useState(example?.text ?? "")
  const [translation, setTranslation] = useState(example?.translation ?? "")
  const [submitting, setSubmitting] = useState(false)
  const [upsellOpen, setUpsellOpen] = useState(false)

  // Prefill from the example each time the modal opens in edit mode.
  React.useEffect(() => {
    if (open) {
      setText(example?.text ?? "")
      setTranslation(example?.translation ?? "")
    }
  }, [open, example?.text, example?.translation])

  const complete = Boolean(text.trim() && translation.trim())
  const insert = (token: string) =>
    setText((current) => `${current}${current ? " " : ""}${token}`)

  // The "use" this example teaches, derived from its first dynamic reference (e.g. Past, Genitive).
  const useLabel = (() => {
    const ref = firstFormReference(text)
    return ref ? tokens.find((token) => token.token === `{{ ${ref} }}`)?.label : undefined
  })()

  const runAction = async (action: () => void | Promise<void>) => {
    setSubmitting(true)
    try {
      await action()
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const onCta = async () => {
    if (!eligible) {
      // Preview is for everyone; contributing is gated. Send them to the upsell.
      setUpsellOpen(true)
      onOpenChange(false)
      return
    }
    if (!complete) return
    const draft = { text: text.trim(), translation: translation.trim() }
    if (editing && onUpdate && example) {
      await runAction(async () => {
        await onUpdate(example, draft)
        trackSubmitted({ headword, edited: true })
      })
    } else if (onSubmit) {
      await runAction(async () => {
        await onSubmit(draft)
        trackSubmitted({ headword })
      })
    }
  }

  // Logged out early-returns to the upsell, so here the viewer is always signed in.
  const ctaLabel = !eligible
    ? `Unlock at level ${MIN_EXAMPLE_LEVEL}`
    : submitting
    ? "Saving…"
    : editing
    ? "Save"
    : "Add example"
  // Eligible-but-incomplete disables; non-eligible stays enabled (it opens the upsell).
  const hasAction = editing ? Boolean(onUpdate) : Boolean(onSubmit)
  const ctaDisabled = eligible && (!complete || !hasAction || submitting)

  // Logged out: skip the editor entirely and show the shared upsell (matches the ratings UX).
  if (!loggedIn) {
    return (
      <UpsellModal
        isOpen={open}
        onClose={() => onOpenChange(false)}
        featureName="codex_example_contribution"
        analyticsProperties={{ headword }}
        icon={<IconStarFilled size={32} />}
        headerGradient="bg-linear-to-br from-earth-400 to-water-600"
        title="Join to contribute examples"
        description="Create a free account to add examples to the codex and earn EXP as you go."
        primaryCta={{ label: "Create an account", href: "/signup", icon: IconUserPlus }}
        secondaryCta={{ label: "Log in", href: loginHref }}
      />
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit example" : "Add an example"}</DialogTitle>
            <DialogDescription>
              A usage sentence for “{headword}”.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Editor column — preview is available to everyone. */}
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">
                  Example (Irish)
                </span>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="e.g. {{ name }} go tapa"
                  className="mt-1 w-full rounded border px-2 py-1"
                  aria-label="Example in Irish"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">
                  Translation (English)
                </span>
                <input
                  value={translation}
                  onChange={(e) => setTranslation(e.target.value)}
                  placeholder="e.g. runs fast"
                  className="mt-1 w-full rounded border px-2 py-1"
                  aria-label="Translation in English"
                />
              </label>

              <div
                className="rounded bg-secondary-50 p-2"
                data-testid="example-preview"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Preview</span>
                  {/* The "use" badge: what form/use this example demonstrates. */}
                  {useLabel && (
                    <span
                      className="rounded bg-earth-50 px-1.5 py-0.5 text-xs text-earth-800"
                      data-testid="use-badge"
                    >
                      {useLabel}
                    </span>
                  )}
                </div>
                <div className="mt-1">
                  {text ? (
                    <>
                      {renderTemplate(
                        text,
                        { headword, name: headword, ...context },
                        { swungDash: true }
                      )}
                      {translation && (
                        <em className="text-muted-foreground">
                          {" "}
                          {translation}
                        </em>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">
                      Start typing to see it rendered.
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Reference column — every supported dynamic string, click to insert. Scrolls; can be long. */}
            <div>
              <span className="text-xs font-medium text-muted-foreground">
                Dynamic strings (click to insert)
              </span>
              <div
                className="mt-1 max-h-[45vh] space-y-1 overflow-y-auto rounded border p-2"
                data-testid="example-tokens"
              >
                {tokens.map((token: ExampleToken) => (
                  <button
                    key={token.token}
                    type="button"
                    onClick={() => insert(token.token)}
                    title={token.label}
                    className="block w-full rounded px-1.5 py-1 text-left text-xs hover:bg-secondary-50"
                  >
                    {/* "this becomes this": {{ name }} → Tá Gaeilge agam */}
                    <span className="flex items-baseline gap-1">
                      <code className="shrink-0 font-bold">{token.token}</code>
                      <span className="text-muted-foreground">→</span>
                      <span className="min-w-0 truncate italic">
                        "{token.value}"
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Add a filter with a pipe, e.g.{" "}
                <code>
                  {tokens[0]?.token.replace(
                    " }}",
                    ` | ${EXAMPLE_FILTERS[0]} }}`
                  )}
                </code>
                . Use ~ for the headword.
              </p>
            </div>
          </div>

          {/* Footer spans the full width; buttons are normal-sized. Delete sits bottom-left in edit
              mode (rating-modal UX); Cancel + the primary action are on the right. */}
          <div className="mt-4 flex items-center justify-between gap-4">
            {editing && onDelete && example ? (
              <button
                type="button"
                onClick={() => runAction(() => onDelete(example))}
                disabled={submitting}
                className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
              >
                Delete example
              </button>
            ) : (
              <span />
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded border px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onCta}
                disabled={ctaDisabled}
                className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
              >
                {ctaLabel}
              </button>
            </div>
          </div>
          {eligible && !editing && !onSubmit && (
            <p className="mt-1 text-right text-xs text-muted-foreground">
              Submitting is coming soon.
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Signed-in-but-under-level: the CTA opens this to explain the level gate. */}
      <UpsellModal
        isOpen={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        featureName="codex_example_contribution"
        analyticsProperties={{ headword }}
        icon={<IconStarFilled size={32} />}
        headerGradient="bg-linear-to-br from-earth-400 to-water-600"
        title={`Reach level ${MIN_EXAMPLE_LEVEL} to contribute`}
        description={`Adding examples unlocks at level ${MIN_EXAMPLE_LEVEL}. You're level ${level}. Keep practicing to unlock it.`}
        primaryCta={{ label: "Play in the arcade", href: arcadeHref }}
      />
    </>
  )
}

/**
 * The subtle "Add new example" CTA to place under each section. Anyone can open it (to preview); the
 * modal gates submitting. Pass the section's token context (buildExampleTokens for that section).
 */
export const AddExampleCta: React.FC<
  ExampleTokenContext & {
    headword: string
    loginHref?: string
    arcadeHref?: string
    onSubmit?: (draft: AddExampleDraft) => void | Promise<void>
  }
> = ({ headword, context, tokens, loginHref, arcadeHref, onSubmit }) => {
  const [open, setOpen] = useState(false)
  const { editing, notify } = useEditing()

  // In an editor preview the CTA is static: route to the editor (Entries panel) instead of opening
  // the contributor modal, so the example is added where it belongs.
  const handleClick = () => {
    if (editing) {
      notify?.("add_example")
      return
    }
    setOpen(true)
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary-600"
      >
        <IconPlus size={14} /> Add new example
      </button>
      {!editing && (
        <AddExampleModal
          open={open}
          onOpenChange={setOpen}
          headword={headword}
          context={context}
          tokens={tokens}
          loginHref={loginHref}
          arcadeHref={arcadeHref}
          onSubmit={onSubmit}
        />
      )}
    </>
  )
}

export default AddExampleCta
